import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth, API } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Loader2, User, Mail, Phone, MapPin, FileText, Briefcase, Sparkles, Calendar, Languages, Award } from "lucide-react";
import { Button } from "./ui/button";

export default function AdminCandidateDetailDialog({ candidateId, open, onOpenChange }) {
  const { authHeader } = useAuth();
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!candidateId) return;
    setLoading(true);
    setData(null);
    try {
      const r = await axios.get(`${API}/admin/candidates/${candidateId}?lang=${encodeURIComponent(lang)}`, { headers: authHeader });
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Failed to load candidate" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && candidateId) load();
    // eslint-disable-next-line
  }, [open, candidateId, lang]);

  const downloadCv = async () => {
    const cvId = data?.candidate?.cv_file_id;
    if (!cvId) return;
    try {
      const r = await axios.get(`${API}/files/${cvId}/download`, { headers: authHeader, responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      a.download = m ? m[1] : `${data?.candidate?.name || "candidate"}_cv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Could not download CV.");
    }
  };

  const c = data?.candidate;
  const tr = (en, fr) => (lang === "fr" ? fr : en);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-candidate-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl inline-flex items-center gap-2">
            <User className="h-5 w-5 text-[#1A8FE3]" />
            {tr("Candidate details", "Détails du candidat")}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {tr("Profile, summary, applications and CV.", "Profil, résumé, candidatures et CV.")}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-16 text-center text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3 text-[#1A8FE3]" />
            <p className="text-sm">{tr("Loading…", "Chargement…")}</p>
          </div>
        )}

        {!loading && data?.error && (
          <div className="py-10 text-center text-rose-600 text-sm">{data.error}</div>
        )}

        {!loading && c && !data?.error && (
          <div className="space-y-5">
            {/* Header */}
            <div className="p-5 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-1">
                <User className="h-3.5 w-3.5" />
                {tr("Candidate", "Candidat")}
              </div>
              <h3 className="font-display text-2xl font-bold text-slate-900" data-testid="admin-cand-name">{c.name || "—"}</h3>
              {c.title && <div className="text-sm text-slate-600 mt-0.5">{c.title}</div>}
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-600">
                {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                {c.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}
                {c.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{c.location}</span>}
                {c.languages && <span className="inline-flex items-center gap-1"><Languages className="h-3 w-3" />{c.languages}</span>}
                {c.created_at && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{tr("Joined", "Inscrit le")} {new Date(c.created_at).toLocaleDateString()}</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {c.cv_file_id && (
                  <Button size="sm" variant="outline" onClick={downloadCv} data-testid="admin-cand-download-cv" className="rounded-full">
                    <FileText className="h-3.5 w-3.5 mr-1" />{tr("Download CV", "Télécharger CV")}
                  </Button>
                )}
                {c.email && (
                  <Button size="sm" variant="outline" asChild className="rounded-full">
                    <a href={`mailto:${c.email}`}><Mail className="h-3.5 w-3.5 mr-1" />{tr("Email", "Envoyer un email")}</a>
                  </Button>
                )}
              </div>
            </div>

            {/* Summary */}
            {data?.summary && (
              <div className="p-4 rounded-xl bg-sky-50 border border-sky-100" data-testid="admin-cand-summary">
                <h4 className="text-[11px] uppercase tracking-widest text-sky-700 font-semibold mb-2 inline-flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" />{tr("Summary", "Résumé")}
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed">{data.summary}</p>
              </div>
            )}

            {/* Profile details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {c.skills && (
                <div className="p-4 rounded-xl border border-slate-200 bg-white">
                  <h4 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
                    <Award className="h-3.5 w-3.5" />{tr("Skills", "Compétences")}
                  </h4>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{c.skills}</p>
                </div>
              )}
              {c.experience_summary && (
                <div className="p-4 rounded-xl border border-slate-200 bg-white">
                  <h4 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{tr("Experience summary", "Résumé d'expérience")}</h4>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{c.experience_summary}</p>
                </div>
              )}
              {c.work_history && (
                <div className="p-4 rounded-xl border border-slate-200 bg-white md:col-span-2">
                  <h4 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{tr("Work history", "Expériences")}</h4>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{c.work_history}</p>
                </div>
              )}
              {c.education && (
                <div className="p-4 rounded-xl border border-slate-200 bg-white md:col-span-2">
                  <h4 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{tr("Education", "Formation")}</h4>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{c.education}</p>
                </div>
              )}
            </div>

            {/* Applications */}
            <div className="p-4 rounded-xl border border-slate-200 bg-white">
              <h4 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3 inline-flex items-center gap-1.5">
                <Briefcase className="h-3.5 w-3.5" />{tr("Applications", "Candidatures")} ({data.applications_count})
              </h4>
              {data.applications.length === 0 ? (
                <p className="text-sm text-slate-500">{tr("No applications yet.", "Aucune candidature pour le moment.")}</p>
              ) : (
                <ul className="space-y-2" data-testid="admin-cand-apps-list">
                  {data.applications.map((a) => (
                    <li key={a.id} className="flex items-center justify-between text-sm">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">{a.job_title || a.job_id}</p>
                        <p className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()} · {tr("status", "statut")}: {a.status}</p>
                      </div>
                      <div className="text-right text-xs text-slate-500 shrink-0 ml-3">
                        {a.match_score != null && <span className="font-semibold text-[#1A8FE3]">{a.match_score}/100</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
